import { useEffect, useRef } from 'react';
import { QueryOptions } from 'apollo-client';
import { useApolloClient } from '@apollo/react-hooks';
import { EXPORT_PER_PAGE_RECORDS, EXPORT_FETCH_CONCURRENCY } from 'constants/index';
import { mapWithConcurrency } from 'utils/concurrency';
import openNotificationWithIcon from 'utils/notification';

interface Options {
  query: unknown,
  queryVariables: Record<string, unknown>,
  totalRecords: number,
  exportDataKey: string,
  extraVariables?: Record<string, unknown>,
  returnData: (data: unknown, selected?: boolean, hasError?: boolean) => void,
  onProgress?: (completedPages: number, totalPages: number) => void,
}

/**
 * Page through an export query and hand back every row.
 *
 * Pages go out through a bounded parallel pool and never enter the Apollo cache.
 * The implementation this replaces chained one `fetchMore` per page through React
 * effects, so page N + 1 only started once page N had landed, and merged each
 * page back into a single cache entry -- meaning Apollo re-normalized and re-read
 * the whole accumulated list every page. On a large export that is hundreds of
 * serialized round trips plus O(n^2) cache work.
 *
 * Fires once per mount. The caller is expected to mount this only while an
 * export is running and unmount it when the rows come back.
 *
 * @param {Options} options query, variables, and the callbacks to report into
 */
export function usePagedExportFetch({
  query,
  queryVariables,
  totalRecords,
  exportDataKey,
  extraVariables,
  returnData,
  onProgress,
}: Options) {
  const client = useApolloClient();

  // These refs keep a re-render or an unmount mid-flight from starting or
  // finishing the fetch twice.
  const started = useRef(false);
  const cancelled = useRef(false);

  const latest = useRef({ query, queryVariables, totalRecords, exportDataKey, extraVariables, returnData, onProgress });
  latest.current = { query, queryVariables, totalRecords, exportDataKey, extraVariables, returnData, onProgress };

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Callers may pin their own perPage inside queryVariables (it wins on the
    // spread), so page count has to be derived from the value actually sent.
    const current = latest.current;
    const baseVariables = {
      perPage: EXPORT_PER_PAGE_RECORDS,
      ...current.extraVariables,
      ...current.queryVariables,
    };
    const perPage = Number(baseVariables.perPage) || EXPORT_PER_PAGE_RECORDS;
    const totalPages = Math.max(1, Math.ceil(current.totalRecords / perPage));
    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

    let completedPages = 0;
    let plannedPages = totalPages;
    let firstError: Error | null = null;

    // Publish a page count before the first request returns, so a progress
    // indicator can appear at 0% with a real total rather than after a round trip.
    current.onProgress?.(0, plannedPages);

    function reportProgress() {
      completedPages += 1;
      if (cancelled.current) return;
      latest.current.onProgress?.(completedPages, plannedPages);
    }

    async function fetchPage(page: number): Promise<unknown[]> {
      const { data } = await client.query({
        query: (current.query as unknown) as QueryOptions['query'],
        variables: { ...baseVariables, page },
        // Export rows are written once and read once. Normalizing them costs
        // more than the fetch itself and leaves them resident for the rest
        // of the session.
        fetchPolicy: 'no-cache',
      });
      return (data?.[current.exportDataKey] ?? []) as unknown[];
    }

    async function fetchAllPages() {
      const rows: unknown[] = [];

      // Fetch page one on its own first. The API clamps perPage to a
      // site-configured maximum and says nothing about having done so, so the page
      // size that came back is the only reliable basis for a page count -- and
      // planning from the requested size instead would stop the export early and
      // write a short file that looks complete.
      const firstPage = await fetchPage(1);
      reportProgress();
      for (let i = 0; i < firstPage.length; i += 1) rows.push(firstPage[i]);

      const honouredPerPage = firstPage.length > 0 ? firstPage.length : perPage;
      if (honouredPerPage < perPage) {
        plannedPages = Math.max(1, Math.ceil(current.totalRecords / honouredPerPage));
        latest.current.onProgress?.(completedPages, plannedPages);
      }

      const remaining = [];
      for (let page = 2; page <= plannedPages; page += 1) remaining.push(page);

      const pages = await mapWithConcurrency(remaining, EXPORT_FETCH_CONCURRENCY, async (page) => {
        // Stop issuing requests once one page has failed or the export was
        // dismissed -- the whole export is abandoned either way. Errors are
        // collected rather than thrown so the pool drains instead of leaving
        // workers in flight against an export nobody is waiting for.
        if (firstError || cancelled.current) return [] as unknown[];

        try {
          const page_rows = await fetchPage(page);
          reportProgress();
          return page_rows;
        } catch (error) {
          if (!firstError) firstError = error as Error;
          return [] as unknown[];
        }
      });

      if (firstError) throw firstError;

      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        for (let j = 0; j < page.length; j += 1) rows.push(page[j]);
      }

      // Last line of defence. If the count says there is more than we collected,
      // keep asking for pages until one comes back empty rather than handing back
      // a partial export as though it were whole.
      let nextPage = plannedPages + 1;
      while (!cancelled.current && rows.length < current.totalRecords) {
        const extra = await fetchPage(nextPage);
        if (extra.length === 0) break;

        for (let i = 0; i < extra.length; i += 1) rows.push(extra[i]);
        nextPage += 1;
        plannedPages = nextPage - 1;
        completedPages = plannedPages;
        latest.current.onProgress?.(completedPages, plannedPages);
      }

      return rows;
    }

    fetchAllPages()
      .then((rows) => {
        if (cancelled.current) return;
        latest.current.returnData(rows, true);
      })
      .catch((error) => {
        if (cancelled.current) return;

        latest.current.returnData([], true, true);

        openNotificationWithIcon({
          type: "error",
          title: error?.message || 'Export failed',
        }, () => {});
      });

    return () => { cancelled.current = true; };
  }, []);
}
